import React from 'react'
import {
  LayoutDashboard,
  Users,
  UserCircle,
  Settings,
  Store,
  DollarSign,
  AlertTriangle,
  CarFront,
  Package,
  MapPin,
  Wrench,
  TrendingUp,
  BarChart3,
  Truck,
  FileText,
  Battery,
  Zap,
  Globe,
  Upload,
  CreditCard,
  Building2,
  Shield,
  Bike,
  ShoppingBag,
  ShieldCheck,
} from 'lucide-react'

interface NavigationItem {
  text: string
  path: string
  icon: React.ReactNode
  keywords?: string[]
  category?: string
  requiredPermission?: {
    service: string
    resource: string
    action: string
  }
  anyOfPermissions?: Array<{
    service: string
    resource: string
    action: string
  }>
}

// Flatten all menu items including nested children into a single array
export const getAllNavigationItems = (): NavigationItem[] => {
  const items: NavigationItem[] = [
    // Dashboard
    {
      text: 'Dashboard',
      path: '/',
      icon: <LayoutDashboard size={20} />,
      category: 'Dashboard',
      keywords: ['home', 'overview', 'main'],
    },

    // Rider Management
    {
      text: 'Riders',
      path: '/rider-management',
      icon: <Truck size={20} />,
      category: 'Rider Management',
      keywords: ['rider', 'delivery', 'personnel', 'driver', 'kyc', 'list'],
      anyOfPermissions: [
        { service: 'rider', resource: 'riders', action: 'read' },
        { service: 'rider', resource: 'riders', action: 'manage' },
        { service: 'rider', resource: 'kyc', action: 'read' },
        { service: 'rider', resource: 'kyc', action: 'verify' },
      ],
    },
    {
      text: 'Rider Vehicle Analytics',
      path: '/rider-management/vehicle-analytics',
      icon: <BarChart3 size={20} />,
      category: 'Rider Management',
      keywords: ['rider', 'vehicle', 'analytics', 'current status', 'deployed', 'returned'],
      anyOfPermissions: [
        { service: 'rider', resource: 'riders', action: 'read' },
        { service: 'rider', resource: 'riders', action: 'manage' },
      ],
    },
    {
      text: 'Rider Attendance Analytics',
      path: '/rider-management/attendance-analytics',
      icon: <BarChart3 size={20} />,
      category: 'Rider Management',
      keywords: ['rider', 'attendance', 'analytics', 'working days', 'WSD', 'LWD'],
      anyOfPermissions: [
        { service: 'rider', resource: 'riders', action: 'read' },
        { service: 'rider', resource: 'riders', action: 'manage' },
      ],
    },
    {
      text: 'Rider Earnings',
      path: '/rider-earnings',
      icon: <DollarSign size={20} />,
      category: 'Rider Management',
      keywords: ['earnings', 'income', 'revenue', 'payment', 'salary'],
      anyOfPermissions: [
        { service: 'rider', resource: 'earnings', action: 'read' },
        { service: 'rider', resource: 'riders', action: 'read' },
      ],
    },
    {
      text: 'Rider Tickets',
      path: '/rider-tickets',
      icon: <AlertTriangle size={20} />,
      category: 'Rider Management',
      keywords: ['tickets', 'issues', 'support', 'rider', 'complaints'],
      anyOfPermissions: [
        { service: 'rider', resource: 'riders', action: 'read' },
        { service: 'rider', resource: 'riders', action: 'manage' },
      ],
    },
    {
      text: 'Order Analytics',
      path: '/order-analytics',
      icon: <BarChart3 size={20} />,
      category: 'Rider Management',
      keywords: ['analytics', 'orders', 'stats', 'reports'],
      anyOfPermissions: [
        { service: 'rider', resource: 'orders', action: 'read' },
        { service: 'rider', resource: 'orders', action: 'manage' },
      ],
    },
    {
      text: 'Orders',
      path: '/orders',
      icon: <ShoppingBag size={20} />,
      category: 'Rider Management',
      keywords: ['orders', 'deliveries', 'tasks'],
      anyOfPermissions: [
        { service: 'rider', resource: 'orders', action: 'read' },
        { service: 'rider', resource: 'orders', action: 'manage' },
      ],
    },
    {
      text: 'MIS Management',
      path: '/mis',
      icon: <FileText size={20} />,
      category: 'Rider Management',
      keywords: ['mis', 'management', 'information', 'system'],
      anyOfPermissions: [
        { service: 'rider', resource: 'mis', action: 'read' },
        { service: 'rider', resource: 'mis', action: 'manage' },
        { service: 'rider', resource: 'earnings', action: 'read' },
        { service: 'rider', resource: 'earnings', action: 'manage' },
      ],
    },
    {
      text: 'Payouts',
      path: '/payouts',
      icon: <CreditCard size={20} />,
      category: 'Rider Management',
            keywords: ['payouts', 'payments', 'disbursements'],
      anyOfPermissions: [
        { service: 'rider', resource: 'payouts', action: 'read' },
        { service: 'rider', resource: 'payouts', action: 'manage' },
        { service: 'rider', resource: 'earnings', action: 'read' },
      ],

    },
    {
      text: 'Manual Collections',
      path: '/manual-collections',
      icon: <CreditCard size={20} />,
      category: 'Rider Management',
      keywords: ['manual', 'collections', 'payments'],
      anyOfPermissions: [
        { service: 'rider', resource: 'manual-collections', action: 'read' },
        { service: 'rider', resource: 'manual-collections', action: 'manage' },
        { service: 'rider', resource: 'collections', action: 'read' },
        { service: 'rider', resource: 'collections', action: 'manage' },
        { service: 'rider', resource: 'earnings', action: 'read' },
        { service: 'rider', resource: 'earnings', action: 'manage' },
      ],
    },
    {
      text: 'Security Deposits',
      path: '/security-deposit',
      icon: <ShieldCheck size={20} />,
      category: 'Rider Management',
      keywords: ['security', 'deposit', 'balance', 'transaction', 'sd', 'deduct', 'refund'],
      anyOfPermissions: [
        { service: 'rider', resource: 'security-deposits', action: 'read' },
        { service: 'rider', resource: 'security-deposits', action: 'manage' },
        { service: 'rider', resource: 'earnings', action: 'read' },
        { service: 'rider', resource: 'earnings', action: 'manage' },
        { service: 'rider', resource: 'riders', action: 'manage' },
      ],
    },
    {
      text: 'Security Deposit Config',
      path: '/security-deposit-config',
      icon: <ShieldCheck size={20} />,
      category: 'Rider Management',
      keywords: ['security', 'deposit', 'config', 'configuration', 'sd'],
      anyOfPermissions: [
        { service: 'rider', resource: 'security-deposit-config', action: 'read' },
        { service: 'rider', resource: 'security-deposit-config', action: 'manage' },
        { service: 'rider', resource: 'earnings', action: 'manage' },
        { service: 'rider', resource: 'riders', action: 'manage' },
      ],
    },
    {
      text: 'Payouts Bulk Upload',
      path: '/payouts/bulk-upload',
      icon: <Upload size={20} />,
      category: 'Rider Management',
      keywords: ['bulk', 'payouts', 'upload', 'csv'],
      anyOfPermissions: [
        { service: 'rider', resource: 'payouts', action: 'manage' },
        { service: 'rider', resource: 'earnings', action: 'manage' },
      ],
    },
    {
      text: 'MIS Bulk Upload',
      path: '/mis/bulk-upload',
      icon: <Upload size={20} />,
      category: 'Rider Management',
      keywords: ['bulk', 'mis', 'upload'],
      anyOfPermissions: [
        { service: 'rider', resource: 'mis', action: 'manage' },
        { service: 'rider', resource: 'earnings', action: 'manage' },
      ],
    },

    // Vehicle Management
    {
      text: 'Vehicle Dashboard',
      path: '/vehicle-dashboard',
      icon: <LayoutDashboard size={20} />,
      category: 'Vehicle Management',
      keywords: ['vehicle', 'dashboard', 'fleet'],
      requiredPermission: { service: 'vehicle', resource: 'vehicles', action: 'read' },
    },
    {
      text: 'Vehicle Inventory',
      path: '/vehicles',
      icon: <Bike size={20} />,
      category: 'Vehicle Management',
      keywords: ['vehicle', 'inventory', 'fleet', 'bikes', 'scooters'],
      requiredPermission: { service: 'vehicle', resource: 'vehicles', action: 'read' },
    },
    {
      text: 'Battery Inventory',
      path: '/batteries',
      icon: <Battery size={20} />,
      category: 'Vehicle Management',
      keywords: ['battery', 'batteries', 'power', 'charge'],
      requiredPermission: { service: 'vehicle', resource: 'batteries', action: 'read' },
    },
    {
      text: 'Charger Inventory',
      path: '/chargers',
      icon: <Zap size={20} />,
      category: 'Vehicle Management',
      keywords: ['charger', 'charging', 'electric'],
      requiredPermission: { service: 'vehicle', resource: 'chargers', action: 'read' },
    },
    {
      text: 'Bulk Vehicle Import',
      path: '/vehicles/bulk-import',
      icon: <Upload size={20} />,
      category: 'Vehicle Management',
      keywords: ['bulk', 'import', 'vehicle', 'upload'],
      requiredPermission: { service: 'vehicle', resource: 'vehicles', action: 'create' },
    },
    {
      text: 'Vehicle Models',
      path: '/vehicle-models',
      icon: <CarFront size={20} />,
      category: 'Vehicle Management',
      keywords: ['models', 'types', 'variants'],
      requiredPermission: { service: 'vehicle', resource: 'models', action: 'read' },
    },
    {
      text: 'OEM Management',
      path: '/oems',
      icon: <Building2 size={20} />,
      category: 'Vehicle Management',
      keywords: ['oem', 'manufacturer', 'brand'],
      requiredPermission: { service: 'vehicle', resource: 'oems', action: 'read' },
    },
    {
      text: 'Hub Management',
      path: '/hubs',
      icon: <MapPin size={20} />,
      category: 'Vehicle Management',
      keywords: ['hub', 'location', 'center', 'depot'],
      requiredPermission: { service: 'vehicle', resource: 'hubs', action: 'read' },
    },
    {
      text: 'Service & Maintenance',
      path: '/services',
      icon: <Wrench size={20} />,
      category: 'Vehicle Management',
      keywords: ['service', 'maintenance', 'repair'],
      requiredPermission: { service: 'vehicle', resource: 'maintenance', action: 'read' },
    },
    {
      text: 'Damage Management',
      path: '/damage',
      icon: <AlertTriangle size={20} />,
      category: 'Vehicle Management',
      keywords: ['damage', 'accident', 'repair'],
      requiredPermission: { service: 'vehicle', resource: 'damage', action: 'read' },
    },
    {
      text: 'Fleet Analytics',
      path: '/vehicle-analytics',
      icon: <BarChart3 size={20} />,
      category: 'Vehicle Management',
      keywords: ['analytics', 'stats', 'reports', 'metrics'],
      requiredPermission: { service: 'vehicle', resource: 'analytics', action: 'read' },
    },

    {
  text: "Spare Parts",
  path: "/spare-parts",
  icon: <Package size={20} />,
  category: "Spare Parts",
  keywords: [
    "spare",
    "parts",
    "inventory",
    "maintenance",
    "supplier",
    "procurement",
  ],
},
    {
  text: "Spare Parts Analytics",
  path: "/spare-parts/analytics",
  icon: <BarChart3 size={20} />,
  category: "Spare Parts",
  keywords: [
    "spare",
    "parts",
    "analytics",
    "reports",
    "metrics",
    "dashboard",
  ],
},
    {
  text: "Spare Parts Maintenance",
  path: "/spare-parts/maintenance",
  icon: <Wrench size={20} />,
  category: "Spare Parts",
  keywords: [
    "spare",
    "parts",
    "maintenance",
    "on-road",
    "on road",
    "preventive",
    "in-house",
    "in house",
    "tickets",
    "technician",
    "service",
  ],
},
    {
  text: "Spare Parts Reports",
  path: "/spare-parts/reports",
  icon: <FileText size={20} />,
  category: "Spare Parts",
  keywords: [
    "spare",
    "parts",
    "reports",
    "export",
    "inventory report",
    "purchase report",
    "maintenance report",
  ],
},
    {
  text: "Spare Parts Operations",
  path: "/spare-parts/operations",
  icon: <Truck size={20} />,
  category: "Spare Parts",
  keywords: [
    "spare",
    "parts",
    "operations",
    "stock request",
    "stock transfer",
    "goods receipt",
    "stock issue",
    "service invoice",
    "service spare usage",
  ],
},

    // Clients & Stores
    {
      text: 'Clients & Stores',
      path: '/clients',
      icon: <Store size={20} />,
      category: 'Clients & Stores',
      keywords: ['clients', 'stores', 'customers', 'business'],
      anyOfPermissions: [
        { service: 'client-store', resource: 'clients', action: 'read' },
        { service: 'client-store', resource: 'stores', action: 'read' },
      ],
    },
    {
      text: 'Client Rider Mapping',
      path: '/client-rider-mappings',
      icon: <Users size={20} />,
      category: 'Clients & Stores',
      keywords: ['mapping', 'assignment', 'allocation'],
      anyOfPermissions: [
        { service: 'client-store', resource: 'clients', action: 'read' },
        { service: 'rider', resource: 'riders', action: 'read' },
      ],
    },

    // Employee Management
    {
      text: 'Employee Management',
      path: '/employees',
      icon: <Users size={20} />,
      category: 'Administration',
      keywords: ['employees', 'staff', 'users', 'team'],
      anyOfPermissions: [
        { service: 'auth', resource: 'employees', action: 'read' },
        { service: 'auth', resource: 'users', action: 'read' },
        { service: 'auth', resource: 'employees', action: 'manage' },
      ],
    },
    {
      text: 'Spare Parts Roles',
      path: '/spare-parts/maintenance-assignments',
      icon: <ShieldCheck size={20} />,
      category: 'Administration',
      keywords: ['spare parts', 'roles', 'assignments', 'ame', 'technician', 'super admin'],
    },

    // Teams & Departments
    {
      text: 'Teams & Departments',
      path: '/teams',
      icon: <Building2 size={20} />,
      category: 'Administration',
      keywords: ['teams', 'departments', 'organization'],
      anyOfPermissions: [
        { service: 'auth', resource: 'teams', action: 'read' },
        { service: 'auth', resource: 'teams', action: 'manage' },
        { service: 'auth', resource: 'departments', action: 'read' },
        { service: 'auth', resource: 'departments', action: 'manage' },
      ],
    },

    // City Management
    {
      text: 'City Dashboard',
      path: '/city-dashboard',
      icon: <LayoutDashboard size={20} />,
      category: 'City Management',
      keywords: ['city', 'dashboard', 'geography'],
      requiredPermission: { service: 'vehicle', resource: 'cities', action: 'read' },
    },
    {
      text: 'City Management',
      path: '/cities',
      icon: <Globe size={20} />,
      category: 'City Management',
      keywords: ['city', 'cities', 'location', 'geography'],
      requiredPermission: { service: 'vehicle', resource: 'cities', action: 'read' },
    },

    // War Room Management
    {
      text: 'War Room',
      path: '/war-room',
      icon: <Globe size={20} />,
      category: 'War Room Management',
      keywords: ['war', 'warroom', 'iot', 'tracking'],
      requiredPermission: { service: 'war-room', resource: 'vehicles', action: 'read' },
    },
    {
      text: 'War Room Charts',
      path: '/war-room/charts',
      icon: <BarChart3 size={20} />,
      category: 'War Room Management',
      keywords: ['charts', 'warroom', 'analytics'],
      requiredPermission: { service: 'war-room', resource: 'charts', action: 'read' },
    },
    {
      text: 'Vehicle Summary',
      path: '/warroom-summary',
      icon: <FileText size={20} />,
      category: 'War Room Management',
      keywords: ['summary', 'vehicle', 'warroom'],
      requiredPermission: { service: 'war-room', resource: 'summary', action: 'read' },
    },
    {
      text: 'Distance Analytics',
      path: '/distance-analytics',
      icon: <TrendingUp size={20} />,
      category: 'War Room Management',
      keywords: ['distance', 'analytics', 'co2', 'warroom'],
      requiredPermission: { service: 'war-room', resource: 'distance-analytics', action: 'read' },
    },

    // Roles & Permissions
    {
      text: 'Roles & Permissions',
      path: '/roles',
      icon: <Shield size={20} />,
      category: 'Administration',
      keywords: ['roles', 'permissions', 'access', 'security'],
      anyOfPermissions: [
        { service: 'auth', resource: 'roles', action: 'read' },
        { service: 'auth', resource: 'permissions', action: 'read' },
      ],
    },

    // Profile
    {
      text: 'Profile',
      path: '/profile',
      icon: <UserCircle size={20} />,
      category: 'User',
      keywords: ['profile', 'account', 'settings', 'user'],
    },

    // Settings
    {
      text: 'Settings',
      path: '/settings',
      icon: <Settings size={20} />,
      category: 'Administration',
      keywords: ['settings', 'configuration', 'preferences'],
    },
  ]

  return items
}
